import React from 'react';
import { Button, Box, Typography, CircularProgress } from '@mui/material';
import { Google } from '@mui/icons-material';
import { useGoogleSignIn } from '@/hooks/useAuth';
import { getGoogleErrorMessage } from '@/utils/google-auth';

interface GoogleSignInButtonProps {
  variant?: 'sign-in' | 'sign-up';
  fullWidth?: boolean;
  disabled?: boolean;
  onError?: (error: string) => void;
}

export const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = ({
  variant = 'sign-in',
  fullWidth = true,
  disabled = false,
  onError
}) => {
  const googleSignInMutation = useGoogleSignIn();

  const handleGoogleSignIn = async () => {
    try {
      await googleSignInMutation.mutateAsync();
    } catch (error: any) {
      const errorMessage = getGoogleErrorMessage(error);
      if (onError) {
        onError(errorMessage);
      }
    }
  };

  const buttonText = variant === 'sign-up' ? 'Sign up with Google' : 'Sign in with Google';

  return (
    <Button
      fullWidth={fullWidth}
      variant="outlined"
      onClick={handleGoogleSignIn}
      disabled={disabled || googleSignInMutation.isLoading}
      sx={{
        py: 1.5,
        borderColor: '#dadce0',
        color: '#3c4043',
        textTransform: 'none',
        fontSize: '14px',
        fontWeight: 500,
        '&:hover': {
          borderColor: '#dadce0',
          backgroundColor: '#f8f9fa',
        },
        '&:disabled': {
          borderColor: '#dadce0',
          color: '#9aa0a6',
        },
      }}
      startIcon={
        googleSignInMutation.isLoading ? (
          <CircularProgress size={18} />
        ) : (
          <Google sx={{ color: '#4285f4' }} />
        )
      }
    >
      {googleSignInMutation.isLoading ? 'Signing in...' : buttonText}
    </Button>
  );
};

export default GoogleSignInButton;